import { playNow } from "@/audio/piano";

/**
 * A vertical raga / scale ladder. Useful for Carnatic content (Sa Re Ga Ma Pa Dha Ni Sa)
 * or any scale that benefits from a cumulative-step view.
 */

export interface RagaStep {
  swara: string; // "Sa", "Ri₁", "Ga₂", etc
  pitch: string; // Western pitch e.g. "C4", "D4"
  notes?: string; // optional caption
  flat?: boolean; // shade differently for flat notes
}

export interface RagaLadderProps {
  title: string;
  steps: RagaStep[];
  /** Western tonic, used for the title only. */
  tonic?: string;
}

export function RagaLadder({ title, steps, tonic }: RagaLadderProps) {
  return (
    <div className="nsm-card p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="font-display text-base font-semibold text-nsm-ink">{title}</div>
          {tonic && <div className="text-[11px] uppercase tracking-wider text-nsm-dim">Sa = {tonic}</div>}
        </div>
        <button
          onClick={async () => {
            for (let i = 0; i < steps.length; i++) {
              await new Promise((r) => setTimeout(r, 320));
              playNow(steps[i].pitch, "8n");
            }
          }}
          className="nsm-btn-primary"
        >
          Sing the ladder
        </button>
      </div>
      <div className="space-y-1.5">
        {[...steps].reverse().map((s, i) => (
          <button
            key={`${s.swara}-${i}`}
            onClick={() => playNow(s.pitch, "4n")}
            className="flex w-full items-center gap-3 rounded-md border border-nsm-border bg-nsm-surface p-2 text-left transition hover:bg-nsm-card"
          >
            <div className="w-12 text-center font-display text-base font-bold text-amber-300">{s.swara}</div>
            <div className="flex-1 text-sm text-nsm-ink">{s.notes ?? ""}</div>
            <div className="text-xs text-nsm-dim">{s.pitch.replace(/-?\d+$/, "").replace("#", "♯").replace(/^([A-G])b$/, "$1♭")}</div>
            <div
              className="ml-2 h-3 w-3 rounded-full"
              style={{ background: s.flat ? "#a78bfa" : "#f59e0b" }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
